/**
 * 마지막 묶음(PICNIC-MB-FIELD-001 v0.2 §6.10 · 2026-10-05) — 무대 넷(소리) · 라디오 하나.
 * LE06 응원 무대 · L23 음악 무대 · L26 밴드 무대 · LE12 작품 전시관 · X09 운동장 보물찾기. 순수 상태(DOM · 시계 없음).
 *
 * 소리는 보드 줄의 ` TN=<센 수> TF=<마지막 음 Hz>`(Bench 확장의 music.setPlayTone — 음 연주 블록 · 멜로디가 지나는 모든 음)로 듣는다 —
 * TN 이 바뀌면 새 음 하나. 소리 효과(웃음소리 등)는 음이 아니라 들리지 않는다 — 응원 무대는 화면이 바뀌는 것도 반응으로 본다.
 */
import type { MbFrame } from "./live";
import { TILT_MG, isBlank } from "./live";
import { bitsOfLeds } from "./font";

/**
 * 새 음이 났나(직전 TN 과 다르면) — 놀이를 연 뒤 처음 본 줄이 기준(놀이 전에 낸 음은 세지 않는다).
 * 아직 음을 하나도 안 낸 보드는 TN 이 없다 — 0 으로 본다(그래야 첫 음이 '새 음'이 된다).
 */
const newTone = (prevN: number | null, frame: MbFrame): boolean =>
  frame.tone !== undefined && prevN !== null && frame.tone.n !== prevN && frame.tone.hz > 20;   // 쉼표는 음이 아니다(멜로디의 '-' 는 playTone(0, …) — 2026-10-05 운영 실측 TF=0)
const toneN = (_prevN: number | null, frame: MbFrame): number => frame.tone?.n ?? 0;

const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"] as const;
/** 주파수 → 음이름(옥타브는 보지 않는다 · 가장 가까운 반음) */
export function noteOf(hz: number): string | null {
  if (!(hz > 20 && hz < 20000)) return null;
  const midi = Math.round(69 + 12 * Math.log2(hz / 440));
  return NOTE_NAMES[((midi % 12) + 12) % 12]!;
}

// ── LE06 응원 무대 — A 소리 · B 소리마다 다른 응원 ─────────────────────────

export const CHEER_WINDOW_MS = 1500;
export interface Cheer {
  readonly clock: number;
  readonly pressA: number | null;
  readonly pressB: number | null;
  /** 반응 모습(음이면 `t<Hz>` · 화면이면 `s<칸>`) */
  readonly a: string | null;
  readonly b: string | null;
  readonly prev: { a: boolean; b: boolean; n: number | null; bits: string } | null;
  /** A · B 응원 반응을 받은 횟수(별 둘 · 셋) */
  readonly count?: number;
}
export const initialCheer = (): Cheer => ({ clock: 0, pressA: null, pressB: null, a: null, b: null, prev: null });
export const cheerDone = (s: Cheer) => s.a !== null && s.b !== null && s.a !== s.b;

export function stepCheer(s: Cheer, dtMs: number, frame: MbFrame | null): Cheer {
  if (!frame) return s;
  const clock = s.clock + dtMs;
  const bits = bitsOfLeds(frame.leds);
  const p = s.prev ?? { a: false, b: false, n: toneN(null, frame), bits };
  let { pressA, pressB, a, b } = s;
  let count = s.count ?? 0;
  if (frame.a && !p.a) pressA = clock;
  if (frame.b && !p.b) pressB = clock;
  const sig = newTone(p.n, frame) ? `t${frame.tone!.hz}` : bits !== p.bits && !isBlank(frame.leds) ? `s${bits}` : null;
  if (sig) {
    // 더 나중에 누른 단추의 반응으로 본다
    const recentA = pressA !== null && clock - pressA <= CHEER_WINDOW_MS ? pressA : -1;
    const recentB = pressB !== null && clock - pressB <= CHEER_WINDOW_MS ? pressB : -1;
    if (recentA >= 0 && recentA >= recentB) { a = sig; pressA = null; count += 1; }
    else if (recentB >= 0) { b = sig; pressB = null; count += 1; }
  }
  return { clock, pressA, pressB, a, b, prev: { a: frame.a, b: frame.b, n: toneN(p.n, frame), bits }, count };
}

// ── L23 음악 무대 — '작은 별' 4마디를 음 순서대로 ──────────────────────────

export const TWINKLE = ["C", "C", "G", "G", "A", "A", "G", "F", "F", "E", "E", "D", "D", "C"] as const;
/** 작은 별 노래 전체(6줄 · 별 둘 = 끝까지 · 별 셋 = 두 번) — 앞 4마디가 TWINKLE */
export const TWINKLE_SONG = [...TWINKLE, "G", "G", "F", "F", "E", "E", "D", "G", "G", "F", "F", "E", "E", "D", ...TWINKLE] as const;
/** j · plays — 노래 전체에서 맞게 친 자리 · 끝까지 친 횟수(별 둘 · 셋) */
export interface Melody { readonly i: number; readonly last: string | null; readonly wrong: number; readonly prevN: number | null; readonly j?: number; readonly plays?: number }
export const initialMelody = (): Melody => ({ i: 0, last: null, wrong: 0, prevN: null });

const advance = (k: number, note: string | null, tune: readonly string[]) => (note === tune[k] ? k + 1 : note === tune[0] ? 1 : 0);

/** 맞는 음이면 한 칸 · 틀리면 처음부터(그 음이 첫 음이면 한 칸에서) — 앞 4마디를 다 치면 해냄. 노래 전체도 따로 센다(끝까지 칠 때마다 plays +1) */
export function stepMelody(s: Melody, frame: MbFrame | null): Melody {
  if (!frame) return s;
  if (!newTone(s.prevN, frame)) return { ...s, prevN: toneN(s.prevN, frame) };
  const note = noteOf(frame.tone!.hz);
  const prevN = frame.tone!.n;
  let j = advance(s.j ?? 0, note, TWINKLE_SONG);
  let plays = s.plays ?? 0;
  if (j >= TWINKLE_SONG.length) { plays += 1; j = 0; }
  const song = { j, plays };
  if (s.i >= TWINKLE.length) return { ...s, last: note, prevN, ...song };
  if (note === TWINKLE[s.i]) return { ...s, i: s.i + 1, last: note, prevN, ...song };
  return { i: note === TWINKLE[0] ? 1 : 0, last: note, wrong: s.wrong + 1, prevN, ...song };
}

// ── L26 밴드 무대 — 로고 · A · B · A+B 네 음 ──────────────────────────────

export type BandKey = "logo" | "a" | "b" | "ab";
export const BAND_KEYS: readonly BandKey[] = ["logo", "a", "b", "ab"];
export const BAND_WINDOW_MS = 1000;
export interface Band {
  readonly clock: number;
  readonly pending: { key: BandKey; at: number } | null;
  readonly notes: Readonly<Partial<Record<BandKey, number>>>;
  readonly prev: { a: boolean; b: boolean; logo: boolean; n: number | null } | null;
  /** 입력에 맞춰 낸 음 수(별 둘 · 셋) */
  readonly count?: number;
}
export const initialBand = (): Band => ({ clock: 0, pending: null, notes: {}, prev: null });
/** 네 입력이 모두 음을 냈고 네 음이 서로 다르다 */
export const bandDone = (s: Band) => BAND_KEYS.every((k) => s.notes[k] !== undefined) && new Set(BAND_KEYS.map((k) => noteOf(s.notes[k]!))).size === 4;

export function stepBand(s: Band, dtMs: number, frame: MbFrame | null): Band {
  if (!frame) return s;
  const clock = s.clock + dtMs;
  const p = s.prev ?? { a: false, b: false, logo: false, n: toneN(null, frame) };
  let pending = s.pending;
  const aNew = frame.a && !p.a, bNew = frame.b && !p.b;
  if ((aNew || bNew) && frame.a && frame.b) pending = { key: "ab", at: clock };
  else if (aNew) pending = { key: "a", at: clock };
  else if (bNew) pending = { key: "b", at: clock };
  if (frame.logo && !p.logo) pending = { key: "logo", at: clock };
  let notes = s.notes;
  let count = s.count ?? 0;
  if (newTone(p.n, frame) && pending && clock - pending.at <= BAND_WINDOW_MS) {
    notes = { ...notes, [pending.key]: frame.tone!.hz };
    pending = null;
    count += 1;
  }
  return { clock, pending, notes, prev: { a: frame.a, b: frame.b, logo: frame.logo, n: toneN(p.n, frame) }, count };
}

// ── LE12 작품 전시관 — 내 부스에 작품 3장 · 관람객 칭찬 스티커 ──────────────

export const SHOWCASE_PIECES = 3;
export const SHOWCASE_HOLD_MS = 1000;
export const SHOWCASE_VISIT_MS = 1500;
export const SHOWCASE_VISITORS = 3;
export const SHOWCASE_VISITORS_MAX = 30;
export interface Showcase { readonly pieces: readonly string[]; readonly cand: string | null; readonly held: number; readonly visitClock: number; readonly visitors: number }
export const initialShowcase = (): Showcase => ({ pieces: [], cand: null, held: 0, visitClock: 0, visitors: 0 });
export const showcaseDone = (s: Showcase) => s.visitors >= SHOWCASE_VISITORS;

/** 화면에 1초 머문 그림(빈 화면 · 이미 건 그림이 아닌)을 한 장씩 건다 — 세 장이 걸리면 관람객이 1.5초마다 한 명씩 들른다 */
export function stepShowcase(s: Showcase, dtMs: number, frame: MbFrame | null): Showcase {
  if (s.pieces.length >= SHOWCASE_PIECES) {
    if (s.visitors >= SHOWCASE_VISITORS_MAX) return s;
    const visitClock = s.visitClock + dtMs;
    return visitClock >= SHOWCASE_VISIT_MS ? { ...s, visitClock: 0, visitors: s.visitors + 1 } : { ...s, visitClock };
  }
  if (!frame || isBlank(frame.leds)) return { ...s, cand: null, held: 0 };
  const bits = bitsOfLeds(frame.leds);
  if (s.pieces.includes(bits)) return { ...s, cand: null, held: 0 };
  const held = bits === s.cand ? s.held + dtMs : dtMs;
  if (held < SHOWCASE_HOLD_MS) return { ...s, cand: bits, held };
  return { ...s, pieces: [...s.pieces, bits], cand: null, held: 0 };
}

// ── X09 운동장 보물찾기 — 놀이터가 보물 보드: 거리만큼 신호가 약해진다 ──────────

export const FIELD_N = 7;
export const TREASURE = { r: 5, c: 5 } as const;
export const HOT_STEP_MS = 400;
export const HOT_FOUND_MS = 1000;
/** 보물 보드가 1초에 두 번 보내는 패킷(숫자 1) — 아이의 '라디오 수신하면 · 신호 세기'가 받는다 */
export const HOT_PING_MS = 500;
export interface HotCold { readonly r: number; readonly c: number; readonly clock: number; readonly held: number; readonly found: boolean; readonly prevN: number | null; readonly toneAt: number | null; readonly t: number }
export const initialHotCold = (): HotCold => ({ r: 1, c: 1, clock: 0, held: 0, found: false, prevN: null, toneAt: null, t: 0 });

/** 보물까지 거리(칸) → 신호 세기(dBm · 가까울수록 크다: 바로 옆 −45 · 멀면 −100) */
export function rssiOf(s: Pick<HotCold, "r" | "c">): number {
  const d = Math.hypot(s.r - TREASURE.r, s.c - TREASURE.c);
  return Math.max(-100, Math.round(-45 - d * 8));
}

/** 숫자 패킷(MakeCode 라디오 판 그대로 — 종류 0 · 시각 · 일련번호 · Int32LE) */
export function numberPacket(value: number, timeMs = 0): number[] {
  const p = new Uint8Array(13);
  const v = new DataView(p.buffer);
  v.setUint8(0, 0);
  v.setInt32(1, timeMs | 0, true);
  v.setInt32(5, 0, true);
  v.setInt32(9, value | 0, true);
  return Array.from(p);
}

/** 기울여 걷는다(벽 없음) · 보물 칸에서 보드가 반응하는 채(화면이 켜졌거나 1초 안에 음) 1초면 찾음 */
export function stepHotCold(s: HotCold, dtMs: number, frame: MbFrame | null): HotCold {
  if (s.found || !frame) return s;
  const t = s.t + dtMs;
  const toneAt = newTone(s.prevN, frame) ? t : s.toneAt;
  const prevN = toneN(s.prevN, frame);
  let { r, c } = s;
  let clock = s.clock + dtMs;
  if (clock >= HOT_STEP_MS) {
    clock = 0;
    const { ax, ay } = frame;
    if (Math.max(Math.abs(ax), Math.abs(ay)) >= TILT_MG) {
      if (Math.abs(ay) >= Math.abs(ax)) r = Math.min(FIELD_N - 1, Math.max(0, r + (ay < 0 ? -1 : 1)));
      else c = Math.min(FIELD_N - 1, Math.max(0, c + (ax < 0 ? -1 : 1)));
    }
  }
  const atTreasure = r === TREASURE.r && c === TREASURE.c;
  const reacting = !isBlank(frame.leds) || (toneAt !== null && t - toneAt <= 1000);
  const held = atTreasure && reacting ? s.held + dtMs : 0;
  return { r, c, clock, held, found: held >= HOT_FOUND_MS, prevN, toneAt, t };
}

/** 마지막 묶음 한 덩어리 */
export interface Stages { readonly cheer: Cheer; readonly melody: Melody; readonly band: Band; readonly showcase: Showcase; readonly hotCold: HotCold }
export const initialStages = (): Stages => ({ cheer: initialCheer(), melody: initialMelody(), band: initialBand(), showcase: initialShowcase(), hotCold: initialHotCold() });
export function stepStages(s: Stages, dtMs: number, frame: MbFrame | null): Stages {
  return {
    cheer: stepCheer(s.cheer, dtMs, frame),
    melody: stepMelody(s.melody, frame),
    band: stepBand(s.band, dtMs, frame),
    showcase: stepShowcase(s.showcase, dtMs, frame),
    hotCold: stepHotCold(s.hotCold, dtMs, frame),
  };
}
